import { useQueries, useQuery } from "@tanstack/react-query";

import { getProjectById, getProjectUsers } from "@synthesis/shared-services/project-gge/projectController/client";
import { getGanttModelsByProjectId } from "@synthesis/shared-services/project-gge/projectGanttController/client";
import { getProjectMeta } from "@synthesis/shared-services/project-gge/projectMetaController/client";

import { projectQueryKeys } from "./project.constants";
import type { GgeProject } from "./project.types";

const emptyProject = (): GgeProject => ({
  id: "",
  ganttModels: [],
  meta: [],
  assignedUsers: [],
});

export const useQueryProject = (id: string | undefined) => {
  const projectQuery = useQuery({
    queryKey: projectQueryKeys.detail(id),
    queryFn: async () => (id ? (await getProjectById({ path: { id } })).data : null),
    enabled: !!id,
    retry: false,
  });

  const queries = useQueries({
    queries: [
      {
        queryKey: projectQueryKeys.primaryModels(id),
        queryFn: async () => (await getGanttModelsByProjectId({ path: { projectId: id! } })).data,
        enabled: !!id && projectQuery.isSuccess,
        retry: false,
      },
      {
        queryKey: projectQueryKeys.meta(id),
        queryFn: async () => (await getProjectMeta({ path: { projectId: id! } })).data,
        enabled: !!id && projectQuery.isSuccess,
        retry: false,
      },
      {
        queryKey: projectQueryKeys.assignedUsers(id),
        queryFn: async () => (await getProjectUsers({ path: { projectId: id! } })).data,
        enabled: !!id && projectQuery.isSuccess,
        retry: false,
      },
    ],
  });

  const isLoading = projectQuery.isLoading || queries.some((q) => q.isLoading);
  const data = projectQuery.data;

  const project: GgeProject = data
    ? ({
        ...data,
        ganttModels: queries[0]?.data || [],
        meta: queries[1]?.data || [],
        assignedUsers: (queries[2]?.data?.assignedUsers ?? []) as GgeProject["assignedUsers"],
        id: data.id || "",
      } as GgeProject)
    : emptyProject();

  return {
    project,
    isLoading,
    isError: projectQuery.isError,
    error: projectQuery.error,
  };
};
